import { CommonModule, CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject,ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import {
  isSameFormulaEvent,
  OrderDisplayType,
  OrdersActionLevel,
  OrdersActionType,
} from '@app/orders/models/orders.model';
import { OrderCustomerUtilsService } from '@app/orders/services/orderCustomer-utils.service';
import { OrdersService } from '@app/orders/services/orders.service';
import { PaymentTransactionService } from '@app/orders/services/payment-transaction.service';
import { AlertService } from '@core/services/alert.service';
import { RightEnum } from '@core/models/rights.model';
import {
  FinancialTransactionRequestDto,
  GetOrderItemDetailsResponseDto,
  OrderArticleSeatResponseDto,
  OrderCustomerResponseDto,
  OrderItemRowSubscriptionResponseDto,
  OrderItemStatusEnumResponseDto,
  OrderItemTypeEnumResponseDto,
  OrderPaymentRequestDto,
  PaymentModeResponseDto,
  PaymentOperationTypeEnumRequestDto,
  PaymentTypeEnumRequestDto,
} from '@core/services/apiclient';
import { select, Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromOrders from '@root-store/orders-store/index';
import { SalesService } from '@sales/services/sales.service';
import { DialogComponent } from '@shared/components';
import {
  AbstractPaymentModeFormComponent,
  AbstractPaymentModeFormUtils,
} from '@shared/components/functional/abstract-payment-mode-form/abstract-payment-mode-form.component';
import { CustomerSearchItem } from '@shared/customer/models/customer-search-item.model';
import { CustomerSearchStateService } from '@shared/customer/services/customer-search-state.service';
import { MomentPipe } from '@shared/pipes/moment.date.format';
import { SharedModule } from '@shared/shared.module';
import { Observable, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { clone } from 'underscore';

type OrderCustomerIdentificationType = 'Normal' | 'Anonymous' | 'Anonymized';
const OrderCustomerIdentificationType: Record<
  OrderCustomerIdentificationType,
  OrderCustomerIdentificationType
> = {
  Normal: 'Normal',
  Anonymous: 'Anonymous',
  Anonymized: 'Anonymized',
};

@Component({
  selector: 'app-orders-folder-payments-dialog',
  templateUrl: './orders-folder-payments-dialog.component.html',
  styleUrls: ['./orders-folder-payments-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    MatDialogModule,
    SharedModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CustomerSearchStateService],
})
export class OrdersFolderPaymentsDialogComponent extends AbstractPaymentModeFormComponent {
  @ViewChild(DialogComponent, { static: true }) dialog: DialogComponent;
  private readonly store = inject(Store<fromOrders.State>);
  private readonly customerSearch = inject(CustomerSearchStateService);
  private readonly salesService = inject(SalesService);
  private readonly datePipe = inject(MomentPipe);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly translate = inject(TranslateService);
  private readonly orderCustomerUtilsService = inject(OrderCustomerUtilsService);
  private readonly paymentTransactionService = inject(PaymentTransactionService);
  private readonly ordersService = inject(OrdersService);
  private readonly alertService = inject(AlertService);

  selectedOrderItem = this.store.selectSignal(fromOrders.selectors.getSelectedOrderItem);

  private readonly orderActionsPayload = toSignal(
    this.store.pipe(select(fromOrders.selectors.selectOrderActionsPayload)),
  );

  private readonly orderActionsPayloadEffect = effect(() => {
    const payload = this.orderActionsPayload();
    if (!payload) {
      this.dialog?.close();
    } else if (payload?.orderActionType === OrdersActionType.Payment) {
      if (payload.displayType === OrderDisplayType.MultipleSubscriptionSelection) {
        this.initSubscriptionPayment(payload);
      } else {
        this.paymentFolders = payload?.items;
        this.paymentseats = payload?.items;
        this.actionLevel = payload?.level;
        this.paymentDisplayType = payload?.displayType;
        this.totalAmount$ = this.getTotalAmount();
        this.initPayer();
        if (!this.dialog?.isDialogOpen()) {
          this.dialog?.open();
        }
      }
    }
  });

  public paymentFolders: GetOrderItemDetailsResponseDto[];
  public selectedCustomer: CustomerSearchItem;
  public paymentTypes = [PaymentTypeEnumRequestDto.Direct];
  public paymentDisplayType: OrderDisplayType;
  public orderDisplayType = OrderDisplayType;
  public paymentseats: OrderArticleSeatResponseDto[];
  public paymentSubscriptionOrderId: number;
  // Séances réservées de la formule, résolues via getSubscriptionOrderItems (mêmes montants réels
  // que la vue liste — les dossiers du volet portent 0 € par séance sur un abonnement) (GEN-6320).
  public reservedSubscriptionItems: OrderItemRowSubscriptionResponseDto[] = [];
  public subscriptionTotalAmount = 0;
  public seatsNumber: number;
  public foldersNumber: number;
  public amountToReturn: number;
  private actionLevel: OrdersActionLevel;
  public RightEnum = RightEnum;
  get customerData() {
    return this.customerSearch.customerData;
  }
  get isLoading() {
    return this.customerSearch.isLoading;
  }
  public selectedCustomerId = 0;

  public get isSingleFolderPayment() {
    return this.paymentDisplayType === this.orderDisplayType.SingleFolderSelection;
  }

  public get isMultiFolderPayment() {
    return this.paymentDisplayType === this.orderDisplayType.MultipleFolderSelection;
  }

  public get isSinlgeSeatPayment() {
    return this.paymentDisplayType === this.orderDisplayType.SingleSeatSelection;
  }

  public get isMultiSeatPayment() {
    return this.paymentDisplayType === this.orderDisplayType.MultipleSeatSelection;
  }

  // Paiement d'une formule entière (GEN-6320) : scope souscription, sous-ensemble réservé encaissé
  // côté backend. Les `paymentFolders` portent les dossiers-évènements réservés pour l'affichage.
  public get isSubscriptionPayment() {
    return this.paymentDisplayType === this.orderDisplayType.MultipleSubscriptionSelection;
  }

  public get hasSameOrderId() {
    return (
      this.paymentFolders?.length &&
      this.paymentFolders.every((folder, index, self) => folder?.orderId === self?.[0]?.orderId)
    );
  }

  protected getPaymentModes(): Observable<PaymentModeResponseDto[]> {
    return this.salesService.getPaymentMode();
  }

  protected getTotalAmount(): Observable<number> {
    if (this.paymentDisplayType === OrderDisplayType.MultipleSubscriptionSelection) {
      return of(this.subscriptionTotalAmount);
    }
    if (!this.paymentFolders?.length && !this.paymentseats?.length) {
      return of(0);
    }
    switch (this.paymentDisplayType) {
      case OrderDisplayType.SingleSeatSelection:
        return of(this.paymentseats[0]?.price?.amount);
      case OrderDisplayType.MultipleSeatSelection:
        this.seatsNumber = this.paymentseats.length;
        return of(
          this.moneyService.calculateTotalAmount(this.paymentseats, (seat) => seat?.price?.amount || 0),
        );
      case OrderDisplayType.SingleFolderSelection:
        this.foldersNumber = 1;
        this.seatsNumber = this.paymentFolders[0]?.quantity;
        return of(this.paymentFolders[0]?.amount);
      case OrderDisplayType.MultipleFolderSelection:
        this.foldersNumber = this.paymentFolders.length;
        this.seatsNumber = (this.paymentFolders || []).reduce((acc, curr) => acc + (curr?.quantity || 0), 0);
        return of(
          this.moneyService.calculateTotalAmount(this.paymentFolders, (folder) => folder?.amount || 0),
        );
    }
  }

  // Paiement d'une formule entière (GEN-6320) : on résout les séances réservées via
  // getSubscriptionOrderItems (mêmes montants réels que la liste — sur un abonnement, les dossiers du
  // volet portent 0 € par séance) AVANT d'ouvrir la pop-up. Le total est ainsi correct dès l'ouverture,
  // ce qui évite l'auto-sélection du mode « Gratuit » (déclenchée quand le total vaut 0) qui provoquait
  // un 403 « Transaction financière invalide » (montant > 0 payé en mode gratuit).
  private initSubscriptionPayment(payload: {
    orderSubscriptions?: { id: number }[];
    items?: GetOrderItemDetailsResponseDto[];
    level: OrdersActionLevel;
    displayType: OrderDisplayType;
  }): void {
    if (this.dialog?.isDialogOpen()) {
      return;
    }
    this.paymentSubscriptionOrderId = payload.orderSubscriptions?.[0]?.id;
    this.actionLevel = payload.level;
    this.paymentDisplayType = payload.displayType;
    if (this.paymentSubscriptionOrderId == null) {
      return;
    }
    // `items` non vide = paiement d'un sous-ensemble d'évènements sélectionnés (action par ligne) : on
    // restreint aux évènements sélectionnés. Vide = toute la formule (bouton « Payer la formule »).
    // Le match se fait via `isSameFormulaEvent` (id OU nom+date), car l'id d'article peut différer entre
    // le dossier du volet et l'item de souscription (GEN-6320).
    const selectedFolders = payload.items ?? [];
    // On mémorise l'id demandé pour ignorer une réponse obsolète (payload plus récent arrivé entre-temps).
    const requestedSubscriptionId = this.paymentSubscriptionOrderId;
    this.ordersService
      .getSubscriptionOrderItems(this.paymentSubscriptionOrderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        if (requestedSubscriptionId !== this.paymentSubscriptionOrderId) {
          return;
        }
        this.reservedSubscriptionItems = (items ?? []).filter(
          (item) =>
            item.statusGroup === OrderItemStatusEnumResponseDto.Reserved &&
            (selectedFolders.length === 0 ||
              selectedFolders.some((folder) => isSameFormulaEvent(item, folder))),
        );
        if (!this.reservedSubscriptionItems.length) {
          // Le bouton « Payer la formule » s'active sur les compteurs du volet (allFolders), résolus
          // depuis une autre source que getSubscriptionOrderItems : en cas de divergence (données
          // obsolètes, timing), on informe l'utilisateur plutôt que d'échouer en silence (GEN-6320).
          this.alertService.showCustom('orders.info.noReservedFormulaSeats', 'warning');
          return;
        }
        this.subscriptionTotalAmount = this.moneyService.calculateTotalAmount(
          this.reservedSubscriptionItems,
          (item) => item.amount || 0,
        );
        this.foldersNumber = this.reservedSubscriptionItems.length;
        this.seatsNumber = this.reservedSubscriptionItems.reduce(
          (acc, item) => acc + (item.quantity || 0),
          0,
        );
        this.totalAmount$ = this.getTotalAmount();
        this.initPayer();
        if (!this.dialog?.isDialogOpen()) {
          this.dialog?.open();
        }
      });
  }

  protected createForm(): void {
    this.form = this.formBuilder.group(
      {
        payer: [null, Validators.required],
        rows: this.formBuilder.array([]),
      },
      {
        validators: [this.hasUnfilledAmountValidator()],
        asyncValidators: [this.paymentModeAmountsSumValidator(this.totalAmount$)],
      },
    );
  }

  private initPayer() {
    switch (this.paymentDisplayType) {
      case OrderDisplayType.MultipleFolderSelection:
      case OrderDisplayType.SingleFolderSelection:
        this.selectedCustomer = this.setPayerCustomer(this.paymentFolders?.[0]?.customer);
        break;
      default:
        // Formule (MultipleSubscriptionSelection) et places : payeur = client du dossier sélectionné.
        this.selectedCustomer = this.setPayerCustomer(this.selectedOrderItem()?.customer);
    }
    this.selectedCustomerId = this.selectedCustomer?.id ?? 0;
    this.customerSearch.addToListIfMissing(this.selectedCustomer);
    this.form?.controls?.payer?.patchValue(this.selectedCustomer);
  }

  private setPayerCustomer(customer: OrderCustomerResponseDto): CustomerSearchItem {
    if (!!customer && !customer?.isAnonymous) {
      const name = `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim();
      const identificationType =
        customer?.identificationType === OrderCustomerIdentificationType.Anonymized ? 'Anonymized' : 'Normal';
      return {
        id: customer?.id,
        name: name || String(customer?.id),
        type: identificationType,
      };
    }
    const anonymousCustomer = this.orderCustomerUtilsService.getAnonymousCustomer(true);
    return {
      id: anonymousCustomer?.id ?? 0,
      name: anonymousCustomer?.displayableName ?? '',
      type: 'Anonymized',
    };
  }

  public onCustomerChange(customer: CustomerSearchItem) {
    this.selectedCustomer = customer;
    this.selectedCustomerId = customer?.id ?? 0;
    this.form.controls?.payer?.patchValue(customer);
  }

  public onSearchTrigger(event: { searchTerm: string }) {
    this.customerSearch.triggerSearch(event.searchTerm);
  }

  private getPaymentItems(rows: { amount: number; mode: PaymentModeResponseDto }[]) {
    return AbstractPaymentModeFormUtils.getDirectPaymentResult(clone(rows), this.amountToReturn).map(
      ({ amount, mode }) => {
        return {
          amount,
          paymentModeId: mode.id,
          paymentOperationType: PaymentOperationTypeEnumRequestDto.Payment,
          payerId: this.form?.controls?.payer?.value?.id,
        } as FinancialTransactionRequestDto;
      },
    );
  }

  public pay() {
    if (this.isSubscriptionPayment) {
      this.paySubscription();
      return;
    }
    this.isSingleFolderPayment || this.isMultiFolderPayment ? this.payOrderItems() : this.paySeats();
  }

  // Paiement de la formule entière : réutilise l'endpoint souscription-aware de la liste
  // (`payOrderItemsAndSubscriptions`). On envoie les `orderItems` = ids des séances réservées (mêmes
  // que la vue liste), avec un mode et un montant cohérents ; le backend n'encaisse que ces places.
  // (GEN-6320).
  private paySubscription() {
    const payerId = this.form?.controls?.payer?.value?.id ?? null;
    const orderFinancialTransactionItems = this.paymentTransactionService.buildPaymentPayloadFromForm(
      this.form,
      payerId,
      this.amountToReturn,
    );
    // Les séances réservées peuvent en théorie relever de plusieurs `orderId` : on les regroupe par
    // order (comme `PaymentTransactionService.buildOrdersMap`) pour ne pas rattacher des items au
    // mauvais order (GEN-6320).
    const orderItemsByOrderId = new Map<number, number[]>();
    for (const item of this.reservedSubscriptionItems) {
      const items = orderItemsByOrderId.get(item.orderId) ?? [];
      items.push(item.id);
      orderItemsByOrderId.set(item.orderId, items);
    }
    const orders = Array.from(orderItemsByOrderId.entries()).map(([orderId, orderItems]) => ({
      orderId,
      orderDetails: {
        orderItems,
        orderSubscriptionIds: [],
      },
    }));
    const request: OrderPaymentRequestDto = {
      orders,
      orderFinancialTransactionItems,
    };
    // Ids du premier order réservé : servent uniquement au rechargement post-succès
    // (`processOrderPaymentSuccess` → `loadOrderItems`).
    const firstItem = this.reservedSubscriptionItems[0];
    this.store.dispatch(
      fromOrders.actions.processSubscriptionPayment({
        request,
        orderId: firstItem?.orderId,
        orderItemId: firstItem?.id,
        level: this.actionLevel,
      }),
    );
  }

  private payOrderItems() {
    const orderItemIds = this.isSingleFolderPayment
      ? [this.paymentFolders?.[0]?.id]
      : this.paymentFolders?.map((folder) => folder?.id);
    const financialTransactionItems = this.getPaymentItems(this.form?.controls?.rows?.value);
    this.store.dispatch(
      fromOrders.actions.processOrderPayment({
        paymentInformation: { financialTransactionItems, orderItemIds },
        orderId: this.paymentFolders?.[0]?.orderId,
        level: this.actionLevel,
      }),
    );
  }

  private paySeats() {
    const seatIds = this.isSinlgeSeatPayment
      ? [this.paymentseats?.[0]?.id]
      : this.paymentseats?.map((seat) => seat?.id);
    const financialTransactionItems = this.getPaymentItems(this.form?.controls?.rows?.value);
    let seatReference = '';
    if (this.isSinlgeSeatPayment) {
      const Seat = this.paymentseats?.[0]?.seatInfos;
      seatReference =
        Seat === undefined
          ? `${this.paymentseats?.[0]?.id}`
          : `${Seat?.accessCode} ${Seat?.rank} ${Seat?.seatNumber}`;
    }

    this.store.dispatch(
      fromOrders.actions.processSeatPayment({
        financialTransactionSeats: {
          financialTransactionItems,
          seatIds,
          orderItemId: this.selectedOrderItem().id,
        },
        orderId: this.selectedOrderItem().orderId,
        orderItemId: this.selectedOrderItem().id,
        level: this.actionLevel,
        seatReference: seatReference,
      }),
    );
  }

  public getSummaryLabelOrderType = (folder: GetOrderItemDetailsResponseDto) =>
    `${this.getFolderName(folder)}
    ${this.datePipe.transform(this.getFolderDate(folder)) ?? ''}
  - ${this.currencyPipe.transform(folder.amount, 'EUR', 'symbol', '0.2-2', 'fr')}`;

  public getSummaryLabelMultiType = (folder: GetOrderItemDetailsResponseDto) =>
    `${this.translate.instant('orders.folderNumber')} ${folder?.id}
    (${this.translate.instant('orders.orderNumber')}${folder?.orderId})
    -${this.getFolderName(folder)}
    ${this.datePipe.transform(this.getFolderDate(folder)) ?? ''}
  - ${this.currencyPipe.transform(folder.amount, 'EUR', 'symbol', '0.2-2', 'fr')}`;

  public getSummaryLabelSubscription = (item: OrderItemRowSubscriptionResponseDto) =>
    `${item?.eventName ?? ''}
    ${this.datePipe.transform(item?.eventDate) ?? ''}
  - ${this.currencyPipe.transform(item?.amount, 'EUR', 'symbol', '0.2-2', 'fr')}`;

  public getSummaryLabelSeats = (seat: OrderArticleSeatResponseDto) => {
    return ` ${seat?.category?.name}
    - ${seat?.seatInfos.accessCode} ${seat?.seatInfos.rank} ${seat?.seatInfos.seatNumber}
    - ${seat?.price?.name}
    - ${this.currencyPipe.transform(seat?.price?.amount, 'EUR', 'symbol', '0.2-2', 'fr')}
    - ${seat?.barCode}`;
  };

  public getFolderName(folder: GetOrderItemDetailsResponseDto) {
    return folder?.type === OrderItemTypeEnumResponseDto.Product
      ? folder?.product?.designation
      : folder?.session?.name;
  }

  public getFolderDate(folder: GetOrderItemDetailsResponseDto) {
    return folder?.type === OrderItemTypeEnumResponseDto.Product ? null : folder?.session?.startDate;
  }

  public close() {
    this.resetActionsPayload();
    this.dialog.close();
  }

  private resetActionsPayload() {
    this.store.dispatch(fromOrders.actions.resetOrderAction());
  }
}
